import React, { useState, useEffect } from "react";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import { baseurl } from "../baseurl";
import axios from "axios";
import Swal from "sweetalert2";
import { FiEdit, FiTrash2, FiXCircle, FiTag, FiCalendar, FiActivity } from "react-icons/fi";

function Coupon() {
  const initialState = {
    name: "",
    type: "percentage",
    discount: "",
    from: new Date(),
    expiry: new Date(),
    usageLimit: "",
    status: "active",
  };

  const [coupon, setCoupon] = useState(initialState);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);

  const fetchCoupons = async () => {
    try {
      const res = await axios.get(`${baseurl}getAllCoupons`);
      if (res.data.success) setCoupons(res.data.coupons);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchCoupons(); }, []);

  const handleChange = (e) => setCoupon({ ...coupon, [e.target.name]: e.target.value });
  const handleDateChange = (name, date) => setCoupon({ ...coupon, [name]: date[0] });

  const handleEditClick = (c) => {
    setIsEditing(true);
    setEditId(c.id);
    setCoupon({
      name: c.coupon_name,
      type: c.coupon_type,
      discount: c.discount_value,
      from: new Date(c.start_date),
      expiry: new Date(c.expiry_date),
      usageLimit: c.usage_limit,
      status: c.status,
    });
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditId(null);
    setCoupon(initialState);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = {
      coupon_name: coupon.name,
      coupon_type: coupon.type,
      discount_value: coupon.discount,
      start_date: coupon.from.toISOString().split("T")[0],
      expiry_date: coupon.expiry.toISOString().split("T")[0],
      usage_limit: coupon.usageLimit,
      status: coupon.status,
    };

    try {
      const res = isEditing 
        ? await axios.put(`${baseurl}updateCoupon/${editId}`, payload)
        : await axios.post(`${baseurl}createCoupon`, payload);

      if (res.data.success) {
        Swal.fire({ icon: "success", title: isEditing ? "Updated" : "Created", timer: 1000, showConfirmButton: false });
        cancelEdit();
        fetchCoupons();
      }
    } catch (err) {
      Swal.fire("Error", "Operation failed", "error");
    } finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Delete Coupon?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      confirmButtonText: "Yes, Delete",
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${baseurl}deleteCoupon/${id}`);
        fetchCoupons();
      } catch (err) { Swal.fire("Error", "Delete failed", "error"); }
    }
  };

  return (
    <>
      <Topbar />
      <Sidebar />
      <main className="main-wrapper bg-light">
        <div className="main-content p-3 p-lg-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="fw-bold text-dark m-0"><FiTag className="me-2"/>Promotional Coupons</h5>
            <div className="badge bg-white text-dark border shadow-sm px-3 py-2">Total: {coupons.length}</div>
          </div>

          <div className="row g-3">
            {/* LEFT: COMPACT FORM */}
            <div className="col-lg-4 col-md-5">
              <div className="card border-0 shadow-sm sticky-top" style={{ top: "85px" }}>
                <div className={`card-header text-white fw-bold py-2 ${isEditing ? 'bg-warning' : 'bg-primary'}`}>
                  {isEditing ? <><FiEdit className="me-2"/>Edit Coupon</> : "Create New"}
                </div>
                <div className="card-body p-3">
                  <form onSubmit={handleSubmit}>
                    <div className="mb-2">
                      <label className="extra-small fw-bold text-muted text-uppercase">Coupon Code</label>
                      <input type="text" className="form-control form-control-sm text-uppercase fw-bold" name="name" required value={coupon.name} onChange={handleChange} placeholder="SUMMER50" />
                    </div>
                    <div className="row g-2 mb-2">
                      <div className="col-6">
                        <label className="extra-small fw-bold text-muted text-uppercase">Type</label>
                        <select className="form-select form-select-sm" name="type" value={coupon.type} onChange={handleChange}>
                          <option value="percentage">Percentage (%)</option>
                          <option value="fixed">Fixed (₹)</option>
                        </select>
                      </div>
                      <div className="col-6">
                        <label className="extra-small fw-bold text-muted text-uppercase">Value</label>
                        <input type="number" className="form-control form-control-sm" name="discount" required value={coupon.discount} onChange={handleChange} />
                      </div>
                    </div>
                    <div className="row g-2 mb-2">
                      <div className="col-6">
                        <label className="extra-small fw-bold text-muted text-uppercase">Valid From</label>
                        <Flatpickr value={coupon.from} onChange={(date) => handleDateChange("from", date)} options={{ dateFormat: "Y-m-d" }} className="form-control form-control-sm bg-white" />
                      </div>
                      <div className="col-6">
                        <label className="extra-small fw-bold text-muted text-uppercase">Expiry Date</label>
                        <Flatpickr value={coupon.expiry} onChange={(date) => handleDateChange("expiry", date)} options={{ dateFormat: "Y-m-d" }} className="form-control form-control-sm bg-white" />
                      </div>
                    </div>
                    <div className="row g-2 mb-3">
                      <div className="col-6">
                        <label className="extra-small fw-bold text-muted text-uppercase">Usage Limit</label>
                        <input type="number" className="form-control form-control-sm" name="usageLimit" value={coupon.usageLimit} onChange={handleChange} />
                      </div>
                      <div className="col-6">
                        <label className="extra-small fw-bold text-muted text-uppercase">Status</label>
                        <select className="form-select form-select-sm" name="status" value={coupon.status} onChange={handleChange}>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>
                    </div>
                    <div className="d-grid gap-2">
                      <button type="submit" className={`btn btn-sm ${isEditing ? 'btn-warning' : 'btn-primary'} fw-bold`} disabled={loading}>
                        {loading ? "..." : isEditing ? "Save Changes" : "Create Coupon"}
                      </button>
                      {isEditing && (
                        <button type="button" className="btn btn-sm btn-light border" onClick={cancelEdit}>Cancel</button>
                      )}
                    </div>
                  </form>
                </div>
              </div>
            </div>

            {/* RIGHT: INFORMATIVE LIST */}
            <div className="col-lg-8 col-md-7">
              <div className="card border-0 shadow-sm overflow-hidden">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0 custom-table">
                    <thead className="bg-white border-bottom">
                      <tr className="extra-small text-muted text-uppercase">
                        <th className="ps-3 py-3">Offer Identity</th>
                        <th>Benefit</th>
                        <th>Timeline</th>
                        <th>Availability</th>
                        <th className="text-end pe-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {coupons.map((c, idx) => {
                        const today = new Date(); today.setHours(0,0,0,0);
                        const isExpired = new Date(c.expiry_date) < today;
                        const displayStatus = (c.status === "active" && isExpired) ? "expired" : c.status;

                        return (
                          <tr key={idx}>
                            <td className="ps-3">
                              <div className="fw-bold text-primary">{c.coupon_name}</div>
                              <div className="extra-small text-muted"><FiActivity size={10}/> Limit: {c.usage_limit}</div>
                            </td>
                            <td>
                              <span className="badge bg-light text-dark border">
                                {c.coupon_type === "percentage" ? `${c.discount_value}% OFF` : `₹${c.discount_value} OFF`}
                              </span>
                            </td>
                            <td>
                              <div className="extra-small d-flex flex-column" style={{lineHeight: 1.1}}>
                                <span><FiCalendar size={10} className="text-success me-1"/> {new Date(c.start_date).toLocaleDateString('en-GB')}</span>
                                <span className="text-muted"><FiCalendar size={10} className="text-danger me-1"/> {new Date(c.expiry_date).toLocaleDateString('en-GB')}</span>
                              </div>
                            </td>
                            <td>
                              <span className={`status-dot ${displayStatus === "active" ? "bg-success" : displayStatus === "expired" ? "bg-warning" : "bg-danger"}`}></span>
                              <span className="extra-small text-capitalize ms-2">{displayStatus}</span>
                            </td>
                            <td className="text-end pe-3">
                              <div className="btn-group shadow-sm border rounded">
                                <button className="btn btn-xs btn-white border-0" onClick={() => handleEditClick(c)}><FiEdit className="text-primary" size={12}/></button>
                                <button className="btn btn-xs btn-white border-0 border-start" onClick={() => handleDelete(c.id)}><FiTrash2 className="text-danger" size={12}/></button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <style>{`
        .extra-small { font-size: 10px; font-weight: 600; letter-spacing: 0.02em; }
        .custom-table thead th { font-size: 10px; padding: 12px 10px; }
        .custom-table tbody td { padding: 10px; font-size: 13px; border-bottom: 1px solid #f1f5f9; }
        .status-dot { height: 8px; width: 8px; border-radius: 50%; display: inline-block; }
        .btn-xs { padding: 4px 8px; line-height: 1; background: white; }
        .btn-white:hover { background-color: #f8fafc; }
        .bg-soft-success { background-color: #e6ffed; color: #22c55e; }
        .sticky-top { z-index: 100; }
      `}</style>
    </>
  );
}

export default Coupon;